import apiClient from '@/lib/api-client';

export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * `user` is intentionally NOT typed as `AuthUser` here: this is the raw wire payload as it
 * comes off the network, not yet validated. Story fu-5 — callers MUST run it through
 * `toAuthUser()` (features/auth/auth-user.mapper.ts) before storing it, so a future backend
 * drift degrades gracefully instead of producing a silent `undefined` deep in the UI.
 */
export interface LoginResponse {
  data: {
    user: unknown;
    accessToken: string;
    expiresIn: number;
  };
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
  return response.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

export async function refreshToken(): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/refresh');
  return response.data;
}

export async function forgotPassword(data: ForgotPasswordRequest): Promise<void> {
  await apiClient.post('/auth/forgot-password', data);
}

export async function resetPassword(data: ResetPasswordRequest): Promise<void> {
  await apiClient.post('/auth/reset-password', data);
}

export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await apiClient.post('/auth/change-password', data);
}

export async function getCurrentUser(): Promise<{ data: unknown }> {
  const response = await apiClient.get<{ data: unknown }>('/auth/me');
  return response.data;
}

export interface UpdatePreferencesRequest {
  language?: string;
  theme?: string;
  fontSize?: string;
}

export async function updatePreferences(data: UpdatePreferencesRequest): Promise<void> {
  await apiClient.patch('/me/preferences', data);
}
